import type {
  ExperimentStatsWithPageInfo,
  ExperimentTest,
  ExperimentTestStats,
} from '@superfunnel/types';
import Badge from '@superfunnel/ui-core/v2/Badge';
import Button from '@superfunnel/ui-core/v2/Button';
import DotStatus from '@superfunnel/ui-core/v2/DotStatus';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@superfunnel/ui-core/v2/DropdownMenu';
import { classNames } from '@superfunnel/utils';
import { ColumnDef } from '@tanstack/react-table';
import {
  Calendar,
  Check,
  EditPencil,
  GraphUp,
  MoreVert,
  NavArrowDown,
  NavArrowUp,
  OpenNewWindow,
  Trophy,
} from 'iconoir-react';
import { useContext, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useDashboardContext } from '@/contexts/DashboardContext';
import * as Sentry from '@sentry/react';
import { Timestamp } from 'firebase/firestore';
import { CustomDataGrid } from '../../components/CustomDataGrid/CustomDataGrid';
import { SnackbarContext } from '../../contexts/SnackbarContext';
import { UserContext } from '../../contexts/UserContext';
import { endExperiment } from '../../services/DynamicPageVariantsService';
import { CompleteExperimentDialog } from './CompleteExperimentDialog';

interface ExperimentRowProps {
  experiment: ExperimentStatsWithPageInfo;
  isExpanded: boolean;
  onToggleExpand: () => void;
  showDynamicPageLink?: boolean;
  onEditExperiment?: () => void;
  onExperimentCompleted?: () => void;
}

// Format date for display
function formatDate(timestamp: any): string {
  if (!timestamp) return 'N/A';
  const date = timestamp.toDate?.() ?? new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
}

// Calculate days since start (or between start and end for completed experiments)
function calculateDuration(startedAt: any, endedAt?: any): number {
  if (!startedAt) return 1;
  const start = startedAt.toDate?.() ?? new Date(startedAt);
  const end = endedAt ? (endedAt.toDate?.() ?? new Date(endedAt)) : Date.now();
  return Math.max(
    1,
    Math.floor((end.valueOf() - start.getTime()) / (1000 * 60 * 60 * 24)),
  );
}

// Get status display color
function getStatusColor(
  experimentStatus: string,
  _statsStatus: string,
): 'green' | 'gray' | 'yellow' {
  if (experimentStatus === 'running') return 'green';
  if (experimentStatus === 'completed') return 'gray';
  return 'yellow';
}

// Traffic split progress bar colors
const trafficColors = [
  'bg-blue-500',
  'bg-orange-500',
  'bg-green-500',
  'bg-purple-500',
  'bg-pink-500',
];

export function ExperimentRow({
  experiment,
  isExpanded,
  onToggleExpand,
  showDynamicPageLink = true,
  onEditExperiment,
  onExperimentCompleted,
}: ExperimentRowProps) {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { selectedAccount } = useContext(UserContext);
  const { dynamicPages } = useDashboardContext();
  const { show } = useContext(SnackbarContext);
  const [isEndingExperiment, setIsEndingExperiment] = useState(false);
  const [isCompleteDialogOpen, setIsCompleteDialogOpen] = useState(false);

  const duration = useMemo(
    () =>
      calculateDuration(
        experiment.experiment.startedAt._seconds * 1000,
        experiment.experiment.endedAt
          ? experiment.experiment.endedAt._seconds * 1000
          : undefined,
      ),
    [experiment.experiment.startedAt, experiment.experiment.endedAt],
  );

  const dynamicPageNames = useMemo(() => {
    return new Map(
      dynamicPages?.map((page) => [
        page.id,
        page.data().name || 'Unnamed Page',
      ]) || [],
    );
  }, [dynamicPages]);

  const isReadyToComplete =
    experiment.experiment.status === 'running' &&
    experiment.status === 'winner_found';

  const confidencePercent = useMemo(() => {
    if (!experiment.tests.length) return 0;
    const maxProbability = Math.max(
      ...experiment.tests.map(
        (t: ExperimentTestStats) => t.primaryMetric.probabilityToBeBest,
      ),
    );
    return Math.round(maxProbability);
  }, [experiment.tests]);

  const handleCompleteExperiment = async (
    _winnerTestId: string,
    winnerWebsiteId: string,
  ) => {
    if (!selectedAccount) return;

    setIsEndingExperiment(true);
    try {
      await endExperiment(
        selectedAccount.id,
        experiment.dynamicPageId,
        experiment.variantId,
        winnerWebsiteId,
      );
      show('Experiment completed successfully', 'info');
      setIsCompleteDialogOpen(false);
      onExperimentCompleted?.();
    } catch (e) {
      show('Failed to complete experiment', 'warn');
      Sentry.captureException(e);
    } finally {
      setIsEndingExperiment(false);
    }
  };

  const handleNavigateToDynamicPage = () => {
    navigate(`/workspace/${slug}/pages/${experiment.dynamicPageId}/details`);
  };

  const isActive = experiment.experiment.status === 'running';

  return (
    <div
      className={classNames(
        'rounded-xl',
        isActive ? 'border border-gray-200 bg-white' : 'bg-gray-100',
      )}
    >
      {/* Row Header */}
      <div
        className="flex cursor-pointer items-center gap-4 px-4 py-4"
        onClick={onToggleExpand}
      >
        {/* Expand/Collapse */}
        <button className="rounded p-1 hover:bg-gray-100">
          {isExpanded ? (
            <NavArrowUp className="h-4 w-4 text-gray-500" />
          ) : (
            <NavArrowDown className="h-4 w-4 text-gray-500" />
          )}
        </button>

        {/* Experiment Info */}
        <div className="flex flex-1 flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900">{experiment.name}</span>
            {isActive && (
              <DotStatus
                color={getStatusColor(
                  experiment.experiment.status,
                  experiment.status,
                )}
                animate={isActive}
              />
            )}
            {isReadyToComplete && (
              <Badge variant="success" size="sm" rounded="full">
                <Check className="h-3 w-3" />
                Ready to Complete
              </Badge>
            )}
          </div>
          {showDynamicPageLink && (
            <button
              className="flex w-fit items-center gap-1 text-sm text-gray-500 hover:text-gray-700 hover:underline"
              onClick={(e) => {
                e.stopPropagation();
                handleNavigateToDynamicPage();
              }}
            >
              {dynamicPageNames.get(experiment.dynamicPageId) ||
                experiment.dynamicPageId}
              <OpenNewWindow className="h-3 w-3" />
            </button>
          )}
        </div>

        {/* Stats */}
        <div className="hidden items-center gap-6 lg:flex">
          <div className="w-20 text-center">
            <div className="text-xs text-gray-500">Status</div>
            <div className="text-sm font-medium text-green-600 capitalize">
              {experiment.experiment.status === 'running'
                ? 'Active'
                : experiment.experiment.status}
            </div>
          </div>
          <div className="w-28 text-center">
            <div className="text-xs text-gray-500">Primary Metric</div>
            <div className="truncate text-sm font-medium text-gray-900">
              {experiment.experiment.metric.name}
            </div>
          </div>
          <div className="w-20 text-center">
            <div className="text-xs text-gray-500">Duration</div>
            <div className="text-sm font-medium text-gray-900">
              {duration} days
            </div>
          </div>
          <div className="w-20 text-center">
            <div className="text-xs text-gray-500">Confidence</div>
            <div
              className={classNames(
                'text-sm font-medium',
                confidencePercent >= 95
                  ? 'text-green-600'
                  : confidencePercent >= 80
                    ? 'text-yellow-600'
                    : 'text-gray-900',
              )}
            >
              {confidencePercent}%
            </div>
          </div>
          <div className="w-16 text-center">
            <div className="text-xs text-gray-500">Variants</div>
            <div className="text-sm font-medium text-gray-900">
              {experiment.tests.length}
            </div>
          </div>
        </div>

        {/* Actions Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreVert className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleNavigateToDynamicPage();
              }}
            >
              <OpenNewWindow className="mr-2 h-4 w-4" />
              View Dynamic Page
            </DropdownMenuItem>
            {onEditExperiment && experiment.experiment.status === 'running' && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onEditExperiment();
                }}
              >
                <EditPencil className="mr-2 h-4 w-4" />
                Edit Experiment
              </DropdownMenuItem>
            )}
            {experiment.experiment.status === 'running' && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setIsCompleteDialogOpen(true);
                }}
                disabled={isEndingExperiment}
              >
                <Trophy className="mr-2 h-4 w-4" />
                Complete Experiment
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Complete Experiment Dialog */}
      <CompleteExperimentDialog
        experiment={experiment}
        open={isCompleteDialogOpen}
        onClose={() => setIsCompleteDialogOpen(false)}
        onComplete={handleCompleteExperiment}
        isCompleting={isEndingExperiment}
      />

      {/* Expanded Content */}
      {isExpanded && (
        <div
          className={classNames(
            'rounded-b-xl border-t border-gray-200 px-6 py-4',
            experiment.experiment.status === 'completed' ? 'bg-gray-50' : '',
          )}
        >
          {/* Experiment Meta */}
          <div className="mb-4 flex items-center gap-6 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Started:{' '}
              {formatDate(
                Timestamp.fromMillis(
                  experiment.experiment.startedAt._seconds * 1000,
                ).toDate(),
              )}
            </div>
            {experiment.experiment.status === 'completed' &&
              experiment.experiment.endedAt && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Ended:{' '}
                  {formatDate(
                    Timestamp.fromMillis(
                      experiment.experiment.endedAt._seconds * 1000,
                    ).toDate(),
                  )}
                </div>
              )}
            {experiment.experiment.status !== 'completed' && (
              <div className="flex items-center gap-2">
                <Trophy className="h-4 w-4" />
                Target Confidence: 95%
              </div>
            )}
          </div>

          {/* Variants Table */}
          <VariantsDataGrid
            experiment={experiment}
            trafficColors={trafficColors}
          />
        </div>
      )}
    </div>
  );
}

interface VariantsDataGridProps {
  experiment: ExperimentStatsWithPageInfo;
  trafficColors: string[];
}

interface VariantRowData {
  testId: string;
  variantName: string;
  isControl: boolean;
  isWinner: boolean;
  experimentStatus: string;
  trafficSplit: number;
  colorIndex: number;
  visitors: number;
  conversions: number;
  conversionRate: number;
  uplift: number | null;
  isPositiveUplift: boolean;
  confidence: number;
}

function VariantsDataGrid({
  experiment,
  trafficColors,
}: VariantsDataGridProps) {
  const data: VariantRowData[] = useMemo(() => {
    return experiment.tests.map(
      (test: ExperimentTestStats, index: number): VariantRowData => {
        const variantName = test.isControl
          ? 'Control'
          : test.description ||
            `Experiment ${String.fromCharCode(65 + index - 1)}`;

        const trafficSplit = test.isControl
          ? experiment.experiment.originalTrafficSplit
          : experiment.experiment.tests.find(
              (t: ExperimentTest) => t.id === test.testId,
            )?.trafficSplit || 0;

        const uplift = test.primaryMetric.relativeUplift;
        const hasUplift = uplift !== null && !test.isControl;
        const isPositiveUplift = hasUplift && uplift > 0;

        return {
          testId: test.testId,
          variantName,
          isControl: test.isControl,
          isWinner: test.testId === experiment.winnerId,
          experimentStatus: experiment.experiment.status,
          trafficSplit,
          colorIndex: index,
          visitors: test.visitors,
          conversions: test.primaryMetric.count,
          conversionRate: test.primaryMetric.rate,
          uplift: test.isControl ? null : uplift,
          isPositiveUplift,
          confidence: test.primaryMetric.probabilityToBeBest,
        };
      },
    );
  }, [experiment]);

  const columns: ColumnDef<VariantRowData>[] = useMemo(
    () => [
      {
        id: 'variantName',
        header: 'Variant Name',
        accessorKey: 'variantName',
        size: 200,
        enableHiding: false,
        cell: ({ row }) => (
          <Badge variant={row.original.isControl ? 'purple' : 'warning'}>
            {row.original.variantName}
          </Badge>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        size: 140,
        enableHiding: false,
        cell: ({ row }) => (
          <Badge variant="success" size="sm" rounded="md">
            {row.original.experimentStatus === 'running'
              ? 'Running'
              : 'Completed'}
          </Badge>
        ),
      },
      {
        id: 'trafficSplit',
        header: 'Traffic Split',
        accessorKey: 'trafficSplit',
        size: 180,
        enableHiding: false,
        cell: ({ row }) => {
          const colorClass =
            trafficColors[row.original.colorIndex % trafficColors.length];
          return (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-900">
                {row.original.trafficSplit}%
              </span>
              <div className="h-2 w-16 overflow-hidden rounded-full bg-gray-200">
                <div
                  className={classNames('h-full rounded-full', colorClass)}
                  style={{ width: `${row.original.trafficSplit}%` }}
                />
              </div>
            </div>
          );
        },
      },
      {
        id: 'visitors',
        header: 'Visitors',
        accessorKey: 'visitors',
        size: 140,
        enableHiding: false,
        cell: ({ row }) => (
          <div className="text-right text-sm text-gray-900">
            {row.original.visitors.toLocaleString()}
          </div>
        ),
      },
      {
        id: 'conversions',
        header: 'Conversions',
        accessorKey: 'conversions',
        size: 150,
        enableHiding: false,
        cell: ({ row }) => (
          <div className="text-right text-sm text-gray-900">
            {row.original.conversions.toLocaleString()}
          </div>
        ),
      },
      {
        id: 'conversionRate',
        header: 'Conv. Rate',
        accessorKey: 'conversionRate',
        size: 140,
        enableHiding: false,
        cell: ({ row }) => (
          <div
            className={classNames(
              'text-right text-sm font-medium',
              row.original.isWinner ? 'text-green-600' : 'text-gray-900',
            )}
          >
            {row.original.conversionRate.toFixed(1)}%
          </div>
        ),
      },
      {
        id: 'uplift',
        header: 'Uplift',
        accessorKey: 'uplift',
        size: 130,
        enableHiding: false,
        cell: ({ row }) => {
          const { uplift, isPositiveUplift, isControl } = row.original;
          const hasUplift =
            uplift !== null && Number.isFinite(uplift) && !isControl;

          if (!hasUplift) {
            return <div className="text-right text-sm text-gray-400">-</div>;
          }

          return (
            <div
              className={classNames(
                'flex items-center justify-end gap-1 text-sm font-medium',
                isPositiveUplift ? 'text-green-600' : 'text-red-600',
              )}
            >
              {isPositiveUplift && <GraphUp className="h-3 w-3" />}
              {isPositiveUplift ? '+' : ''}
              {uplift.toFixed(1)}%
            </div>
          );
        },
      },
      {
        id: 'confidence',
        header: 'Confidence',
        accessorKey: 'confidence',
        size: 140,
        enableHiding: false,
        cell: ({ row }) => {
          return (
            <div
              className={classNames(
                'text-right text-sm font-medium',
                row.original.confidence >= 95
                  ? 'text-green-600'
                  : 'text-gray-900',
              )}
            >
              {Math.round(row.original.confidence)}%
            </div>
          );
        },
      },
    ],
    [trafficColors],
  );

  return (
    <div className="overflow-hidden rounded-lg">
      <CustomDataGrid
        logicalName="ExperimentVariants"
        columns={columns}
        data={data}
        virtualized={false}
      />
    </div>
  );
}
